import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorIcon,
  CreditCard as CreditCardIcon,
  HourglassEmpty as HourglassIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  HandymanOutlined as ManualIcon,
  OpenInNew as OpenInNewIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import adminPaymentAuthService from '../../services/adminPaymentAuthService';

// ═══════════════════════════════════════════════════════════════
// Constantes
// ═══════════════════════════════════════════════════════════════

const STATUS_OPTIONS = [
  { value: '', labelKey: 'admin3DSManagement.filters.allStatuses' },
  { value: 'pending_auth', labelKey: 'admin3DSManagement.filters.pendingAuth' },
  { value: 'authenticated', labelKey: 'admin3DSManagement.filters.authenticated' },
  { value: 'completed', labelKey: 'admin3DSManagement.filters.completed' },
  { value: 'expired', labelKey: 'admin3DSManagement.filters.expired' },
  { value: 'failed', labelKey: 'admin3DSManagement.filters.failed' },
  { value: 'cancelled', labelKey: 'admin3DSManagement.filters.cancelled' }
];

const STATUS_CHIP_CONFIG = {
  pending_auth: { color: 'warning', icon: <HourglassIcon fontSize="small" /> },
  authenticated: { color: 'info', icon: <CheckCircleIcon fontSize="small" /> },
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  expired: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  failed: { color: 'error', icon: <CancelIcon fontSize="small" /> },
  cancelled: { color: 'default', icon: <CancelIcon fontSize="small" /> }
};

// ═══════════════════════════════════════════════════════════════
// Composant principal
// ═══════════════════════════════════════════════════════════════

const Admin3DSManagementPage = () => {
  const { t, i18n } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const dateLocale = i18n?.language?.startsWith('fr') ? fr : enUS;

  // State — Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // State — List
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // State — Action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRequest, setMenuRequest] = useState(null);

  // State — Manual handling dialog
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualNotes, setManualNotes] = useState('');
  const [manualTarget, setManualTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // State — Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  // ═════════════════════════════════════════════════
  // Data fetching
  // ═════════════════════════════════════════════════

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await adminPaymentAuthService.adminStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch 3DS stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminPaymentAuthService.adminList({
        status: statusFilter || undefined,
        search: searchQuery.trim() || undefined,
        page,
        limit: 20
      });
      setRequests(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch 3DS list:', err);
      setError(t('admin3DSManagement.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, page, t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRefresh = () => {
    fetchStats();
    fetchList();
  };

  // ═════════════════════════════════════════════════
  // Actions
  // ═════════════════════════════════════════════════

  const handleMenuOpen = (event, request) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRequest(request);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRequest(null);
  };

  const handleResend = async (request) => {
    handleMenuClose();
    try {
      setActionLoading(true);
      await adminPaymentAuthService.adminResend(request.id);
      enqueueSnackbar(t('admin3DSManagement.actions.resendSuccess'), { variant: 'success' });
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || t('admin3DSManagement.actions.resendError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenManualDialog = (request) => {
    handleMenuClose();
    setManualTarget(request);
    setManualNotes('');
    setManualDialogOpen(true);
  };

  const handleConfirmManual = async () => {
    if (!manualTarget) return;
    try {
      setActionLoading(true);
      await adminPaymentAuthService.adminMarkManual(manualTarget.id, manualNotes);
      enqueueSnackbar(t('admin3DSManagement.actions.manualSuccess'), { variant: 'success' });
      setManualDialogOpen(false);
      setManualTarget(null);
      setManualNotes('');
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || t('admin3DSManagement.actions.manualError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenStatusDialog = (request) => {
    handleMenuClose();
    setStatusTarget(request);
    setSelectedNewStatus('');
    setStatusReason('');
    setStatusDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusTarget || !selectedNewStatus) return;
    try {
      setActionLoading(true);
      await adminPaymentAuthService.adminUpdateStatus(
        statusTarget.id,
        selectedNewStatus,
        statusReason
      );
      enqueueSnackbar(t('admin3DSManagement.actions.statusSuccess'), { variant: 'success' });
      setStatusDialogOpen(false);
      setStatusTarget(null);
      setSelectedNewStatus('');
      setStatusReason('');
      fetchList();
      fetchStats();
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || t('admin3DSManagement.actions.statusError'),
        { variant: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ═════════════════════════════════════════════════
  // Helpers
  // ═════════════════════════════════════════════════

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: dateLocale });
  };

  const renderTimeRemaining = (timeRemaining) => {
    if (!timeRemaining) return '-';
    if (timeRemaining.expired) {
      return (
        <Chip
          size="small"
          label={t('admin3DSManagement.table.expired')}
          color="error"
          variant="outlined"
          icon={<ErrorIcon />}
        />
      );
    }
    const label = timeRemaining.hours > 0
      ? t('admin3DSManagement.table.timeHoursMinutes', { hours: timeRemaining.hours, minutes: timeRemaining.minutes })
      : t('admin3DSManagement.table.timeMinutes', { minutes: timeRemaining.minutes });

    const color = timeRemaining.hours < 24 ? 'warning' : 'info';
    return <Chip size="small" label={label} color={color} variant="outlined" icon={<AccessTimeIcon />} />;
  };

  const renderNotificationHistory = (history) => {
    if (!history || history.length === 0) {
      return <Typography variant="caption" color="text.secondary">-</Typography>;
    }
    return (
      <Stack spacing={0.5}>
        {history.map((item, idx) => (
          <Tooltip key={idx} title={formatDate(item.sentAt)}>
            <Chip
              size="small"
              variant="outlined"
              label={t(`admin3DSManagement.notifTypes.${item.type}`)}
              sx={{ fontSize: '0.7rem' }}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  };

  const canResend = (request) => {
    return ['pending_auth'].includes(request.status);
  };

  const canMarkManual = (request) => {
    return ['expired', 'failed'].includes(request.status) && !request.manually_handled;
  };

  // ═════════════════════════════════════════════════
  // Render stats cards
  // ═════════════════════════════════════════════════

  const StatCard = ({ title, value, color, icon }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {statsLoading ? <CircularProgress size={24} /> : (value ?? '-')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  // ═════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon fontSize="large" color="primary" />
            {t('admin3DSManagement.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('admin3DSManagement.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('admin3DSManagement.refresh')}>
          <IconButton onClick={handleRefresh} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin3DSManagement.stats.pending')}
            value={stats?.pending}
            color="warning"
            icon={<HourglassIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin3DSManagement.stats.expiringSoon')}
            value={stats?.expiringSoon}
            color="error"
            icon={<ScheduleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin3DSManagement.stats.expiredUnhandled')}
            value={stats?.expiredUnhandled}
            color="error"
            icon={<ErrorIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin3DSManagement.stats.completed')}
            value={stats?.completed}
            color="success"
            icon={<TrendingUpIcon />}
          />
        </Grid>
      </Grid>

      {/* Secondary stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.overdue')}
            value={stats?.overdue}
            color="error"
            icon={<WarningIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.authenticated')}
            value={stats?.authenticated}
            color="info"
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.expired')}
            value={stats?.expired}
            color="error"
            icon={<ErrorIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.failed')}
            value={stats?.failed}
            color="error"
            icon={<CancelIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.cancelled')}
            value={stats?.cancelled}
            color="default"
            icon={<CancelIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title={t('admin3DSManagement.stats.total')}
            value={stats?.total}
            color="primary"
            icon={<CreditCardIcon />}
          />
        </Grid>
      </Grid>

      {/* Alert for urgent items */}
      {stats && (stats.expiredUnhandled > 0 || stats.expiringSoon > 0) && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <AlertTitle>{t('admin3DSManagement.alerts.urgentTitle')}</AlertTitle>
          {stats.expiredUnhandled > 0 && (
            <Typography variant="body2">
              {t('admin3DSManagement.alerts.expiredUnhandled', { count: stats.expiredUnhandled })}
            </Typography>
          )}
          {stats.expiringSoon > 0 && (
            <Typography variant="body2">
              {t('admin3DSManagement.alerts.expiringSoon', { count: stats.expiringSoon })}
            </Typography>
          )}
        </Alert>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('admin3DSManagement.filters.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('admin3DSManagement.filters.status')}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={t('admin3DSManagement.filters.searchPlaceholder', 'Rechercher par ID, litige, email...')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            sx={{ minWidth: 280 }}
          />
        </Stack>
      </Paper>

      {/* Data Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('admin3DSManagement.table.noResults')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                    <TableCell>{t('admin3DSManagement.table.id')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.dispute')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.renter')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.owner')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.amount')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.status')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.timeRemainingHeader')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.notifications')}</TableCell>
                    <TableCell>{t('admin3DSManagement.table.createdAt')}</TableCell>
                    <TableCell align="right">{t('admin3DSManagement.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((req) => {
                    const chipConfig = STATUS_CHIP_CONFIG[req.status] || { color: 'default', icon: null };
                    return (
                      <TableRow key={req.id} hover>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            #{req.id?.substring(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="caption" fontFamily="monospace">
                              #{req.dispute_id?.substring(0, 8)}
                            </Typography>
                            {req.listingTitle && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                                {req.listingTitle}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{req.renter?.name || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{req.renter?.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{req.owner?.name || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{req.owner?.email || ''}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color="primary.main">
                            {req.amount ? `${req.amount} ${req.currency || 'EUR'}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={chipConfig.icon}
                            label={t(`admin3DSManagement.statuses.${req.status}`)}
                            size="small"
                            color={chipConfig.color}
                          />
                          {req.manually_handled && (
                            <Chip
                              icon={<ManualIcon fontSize="small" />}
                              label={t('admin3DSManagement.statuses.manuallyHandled')}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {renderTimeRemaining(req.timeRemaining)}
                        </TableCell>
                        <TableCell>
                          {renderNotificationHistory(req.notificationHistory)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(req.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={t('admin3DSManagement.actions.openDispute')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => window.open(`/disputes/${req.dispute_id}`, '_blank')}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, req)}
                              disabled={actionLoading}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, p) => setPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleOpenStatusDialog(menuRequest)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('admin3DSManagement.actions.changeStatus', 'Changer le statut')}</ListItemText>
        </MenuItem>
        {menuRequest && canResend(menuRequest) && (
          <MenuItem onClick={() => handleResend(menuRequest)}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('admin3DSManagement.actions.resend')}</ListItemText>
          </MenuItem>
        )}
        {menuRequest && canMarkManual(menuRequest) && (
          <MenuItem onClick={() => handleOpenManualDialog(menuRequest)}>
            <ListItemIcon><ManualIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('admin3DSManagement.actions.markManual')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Manual Handling Dialog */}
      <Dialog
        open={manualDialogOpen}
        onClose={() => !actionLoading && setManualDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ManualIcon color="info" />
          {t('admin3DSManagement.manualDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin3DSManagement.manualDialog.description')}
          </Typography>
          {manualTarget && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{t('admin3DSManagement.manualDialog.requestId')}:</strong>{' '}
                {manualTarget.id?.substring(0, 12)}...
              </Typography>
              <Typography variant="body2">
                <strong>{t('admin3DSManagement.manualDialog.renter')}:</strong>{' '}
                {manualTarget.renter?.name || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>{t('admin3DSManagement.manualDialog.amount')}:</strong>{' '}
                {manualTarget.amount} {manualTarget.currency || 'EUR'}
              </Typography>
            </Paper>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('admin3DSManagement.manualDialog.notesLabel')}
            placeholder={t('admin3DSManagement.manualDialog.notesPlaceholder')}
            value={manualNotes}
            onChange={(e) => setManualNotes(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${manualNotes.length}/2000`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)} disabled={actionLoading}>
            {t('admin3DSManagement.manualDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmManual}
            variant="contained"
            color="primary"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <ManualIcon />}
          >
            {t('admin3DSManagement.manualDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => !actionLoading && setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          {t('admin3DSManagement.statusDialog.title', 'Changer le statut')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin3DSManagement.statusDialog.description', 'Forcez le statut de cette demande d\'authentification 3DS. Cette action est traçée et irréversible.')}
          </Typography>
          {statusTarget && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{t('admin3DSManagement.statusDialog.currentStatus', 'Statut actuel')}:</strong>{' '}
                <Chip
                  size="small"
                  label={t(`admin3DSManagement.statuses.${statusTarget.status}`)}
                  color={(STATUS_CHIP_CONFIG[statusTarget.status] || {}).color || 'default'}
                />
              </Typography>
              <Typography variant="body2">
                <strong>{t('admin3DSManagement.manualDialog.requestId')}:</strong>{' '}
                {statusTarget.id?.substring(0, 12)}...
              </Typography>
            </Paper>
          )}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('admin3DSManagement.statusDialog.newStatus', 'Nouveau statut')}</InputLabel>
            <Select
              value={selectedNewStatus}
              label={t('admin3DSManagement.statusDialog.newStatus', 'Nouveau statut')}
              onChange={(e) => setSelectedNewStatus(e.target.value)}
            >
              {STATUS_OPTIONS.filter(opt => opt.value !== '').map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label={t('admin3DSManagement.statusDialog.reasonLabel', 'Raison (optionnel)')}
            placeholder={t('admin3DSManagement.statusDialog.reasonPlaceholder', 'Pourquoi changez-vous ce statut ?')}
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${statusReason.length}/1000`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={actionLoading}>
            {t('admin3DSManagement.manualDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            variant="contained"
            color="primary"
            disabled={actionLoading || !selectedNewStatus}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {t('admin3DSManagement.statusDialog.confirm', 'Confirmer le changement')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Admin3DSManagementPage;
